import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ContentContext } from "../contexts/ContentContext"
import { AiOutlineClear } from "react-icons/ai"
import { IoSearch } from "react-icons/io5"
import { MdRestartAlt } from "react-icons/md"
import { requestPaginationSearchMembers, requestPaginationSearchMembersBanned } from "../requests/members_requests"
import { pageLoadingActive } from "./Background"
import { AccountContext } from "../contexts/AccountContexts"



export const MemberSearchComponent = ({ getData, currentPage, setCurrentPage, entriesPerPage, setEntriesPerPage, sortConfig, setSortConfig, setTotalPages, setTotalCount, banned = false, gameBanned = false }) => {
    const { t } = useTranslation(['edit', 'common'])
    const { user } = useContext(AccountContext)
    const { servers, openErrorModal } = useContext(ContentContext)

    const [name, setName] = useState("")
    const [nameMode, setNameMode] = useState(1)
    const [email, setEmail] = useState("")
    const [emailMode, setEmailMode] = useState(1)
    const [characterName, setCharacterName] = useState("")
    const [characterNameMode, setCharacterNameMode] = useState(1)
    const [selectedServer, setSelectedServer] = useState("All")

    const [firstSearch, setFirstSearch] = useState(false)
    const [isAdvanced, setIsAdvanced] = useState(false)
    const [doSearch, setDoSearch] = useState(false)
    const [isDefault, setIsDefault] = useState(true)
    const [isPerma, setIsPerma] = useState(false)

    const [isHighlight, setIsHighlight] = useState(false)

    useEffect(() => {
        if (isHighlight) {
            setIsHighlight(false)
        }
    }, [name, nameMode, email, emailMode, characterName, characterNameMode, selectedServer])


    useEffect(() => {
        let searchParams = getPrevSearchParams()
        if (searchParams) {
            searchParams = JSON.parse(searchParams)
            setIsDefault(searchParams.isDefault)
            setName(searchParams.name)
            setNameMode(parseInt(searchParams.nameMode))
            setEmail(searchParams.email)
            setEmailMode(parseInt(searchParams.emailMode))
            setCharacterName(searchParams.characterName)
            setCharacterNameMode(parseInt(searchParams.characterNameMode))
            setSelectedServer(searchParams.selectedServer)
            setCurrentPage(parseInt(searchParams.currentPage))
            setEntriesPerPage(parseInt(searchParams.entriesPerPage))
            setSortConfig({ key: searchParams.sortByIndex, direction: searchParams.order })
            setIsPerma(searchParams.isPerma)

            setFirstSearch(true)
        }
        else {
            handleDefault()
        }
    }, [])


    useEffect(() => {
        if (doSearch && !isAdvanced) {
            handleSearch(isDefault)
        }
    }, [doSearch])


    useEffect(() => {
        if (firstSearch) {
            setDoSearch(true)
            setFirstSearch(false)
        }
    }, [firstSearch])


    useEffect(() => {
        if (!firstSearch) {
            setDoSearch(true)
        }
    }, [entriesPerPage, sortConfig.key, sortConfig.direction])

    useEffect(() => {
        if (!firstSearch) {
            if (!doSearch) {
                setDoSearch(true)
            }
        }
    }, [currentPage])

    const handleBanned = (default_) => {
        if (banned) {
            return requestPaginationSearchMembersBanned({
                isDefault: default_,
                name: name,
                nameMode: nameMode,
                email: email,
                emailMode: emailMode,
                characterName: characterName,
                characterNameMode: characterNameMode,
                selectedServer: selectedServer === "All" ? selectedServer : servers[selectedServer]?.title,
                currentPage: currentPage,
                entriesPerPage: entriesPerPage,
                sortByIndex: sortConfig.key,
                order: sortConfig.direction,
                isPerma: isPerma
            })
        }
        else {
            return requestPaginationSearchMembers({
                isDefault: default_,
                name: name,
                nameMode: nameMode,
                email: email,
                emailMode: emailMode,
                characterName: characterName,
                characterNameMode: characterNameMode,
                selectedServer: selectedServer === "All" ? selectedServer : servers[selectedServer]?.title,
                currentPage: currentPage,
                entriesPerPage: entriesPerPage,
                sortByIndex: sortConfig.key,
                order: sortConfig.direction,
                isPerma: isPerma
            })
        }
    }


    const handleSearch = (default_ = false) => {
        pageLoadingActive(true)

        handleBanned(default_)
            .then((data) => {
                getData(data.members)
                setTotalPages(data.total_pages)
                setTotalCount(data.total_count)
                saveSearchParams(default_)
                // setIsAdvanced(false)
                setIsHighlight(true)
            })
            .catch((error) => {
                openErrorModal(error)
                console.error(error.status, error.message)
            })
            .finally(() => {
                setDoSearch(false)
                pageLoadingActive(false)
            })
    }


    const handleRegularSearch = () => {
        setCurrentPage(1)
        setSortConfig({ key: null, direction: "desc" })
        setIsDefault(false)
        setIsAdvanced(false)
        handleSearch(false)
    }

    const handleDefault = () => {
        setCurrentPage(1)
        setSortConfig({ key: null, direction: "desc" })
        handleClear()
        setIsDefault(true)
        setIsAdvanced(false)
        handleSearch(true)
    }

    const handleClear = () => {
        setName("")
        setNameMode(1)
        setEmail("")
        setEmailMode(1)
        setCharacterName("")
        setCharacterNameMode(1)
        setSelectedServer("All")

        removeSearchParams()
    }


    const saveSearchParams = (default_) => {
        const searchParams = {
            isDefault: default_,
            name: default_ ? "" : name,
            nameMode: default_ ? 1 : nameMode,
            email: default_ ? "" : email,
            emailMode: default_ ? 1 : emailMode,
            characterName: default_ ? "" : characterName,
            characterNameMode: default_ ? 1 : characterNameMode,
            selectedServer: default_ ? "All" : selectedServer,
            currentPage: currentPage,
            entriesPerPage: entriesPerPage,
            sortByIndex: sortConfig.key,
            order: sortConfig.direction,
            isPerma: isPerma
        }
        if (gameBanned) {
            localStorage.setItem(user.id + "_prevMemberSearchParamsBannedGame", JSON.stringify(searchParams))
        }
        else if (banned) {
            localStorage.setItem(user.id + "_prevMemberSearchParamsBanned", JSON.stringify(searchParams))
        }
        else {
            localStorage.setItem(user.id + "_prevMemberSearchParams", JSON.stringify(searchParams))
        }
    }

    const removeSearchParams = () => {
        if (gameBanned) {
            localStorage.removeItem(user.id + "_prevMemberSearchParamsBannedGame")
        }
        else if (banned) {
            localStorage.removeItem(user.id + "_prevMemberSearchParamsBanned")
        }
        else {
            localStorage.removeItem(user.id + "_prevMemberSearchParams")
        }
    }

    const getPrevSearchParams = () => {
        if (gameBanned) {
            const searchParams = localStorage.getItem(user.id + "_prevMemberSearchParamsBannedGame")
            return searchParams
        }
        else if (banned) {
            const searchParams = localStorage.getItem(user.id + "_prevMemberSearchParamsBanned")
            return searchParams
        }
        else {
            const searchParams = localStorage.getItem(user.id + "_prevMemberSearchParams")
            return searchParams
        }
    }


    return (
        <div className="flex lg:flex-row flex-col justify-center gap-4 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 w-full rounded-lg p-5 ">
            <div className="flex flex-col gap-1">
                <h1>{t('Name')}</h1>
                <input type="input" value={name} onChange={(e) => { setName(e.target.value) }} placeholder={t("Type name")} className={`input input-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-[1px] ${name !== "" ? "border-blue-400" : "border-gray-300 dark:border-gray-500"}`} />
                <select required value={nameMode || 1} onChange={(e) => setNameMode(e.target.value)} className="select select-sm select-bordered select-md w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300" >
                    <option value={1}>{t('Contains')}</option>
                    <option value={2}>{t('Exact')}</option>
                </select>
            </div>

            <div className="flex flex-col gap-1">
                <h1>{t('E-mail')}</h1>
                <input type="input" value={email} onChange={(e) => { setEmail(e.target.value) }} placeholder={t('Type E-mail')} className={`input input-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-[1px] ${email !== "" ? "border-blue-400" : "border-gray-300 dark:border-gray-500"}`} />
                <select required value={emailMode || 1} onChange={(e) => setEmailMode(e.target.value)} className="select select-sm select-bordered select-md w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300" >
                    <option value={1}>{t('Contains')}</option>
                    <option value={2}>{t('Exact')}</option>
                </select>
            </div>

            <div className="flex flex-col gap-1">
                <h1>{t('Character Name')}</h1>
                <input type="input" value={characterName} onChange={(e) => { setCharacterName(e.target.value) }} placeholder={t('Type Character Name')} className={`input input-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-[1px] ${characterName !== "" ? "border-blue-400" : "border-gray-300 dark:border-gray-500"}`} />
                <select required value={characterNameMode || 1} onChange={(e) => setCharacterNameMode(e.target.value)} className="select select-sm select-bordered select-md w-full bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300" >
                    <option value={1}>{t('Contains')}</option>
                    <option value={2}>{t('Exact')}</option>
                </select>
            </div>

            <div className="flex flex-col gap-1">
                <h1>{t('Server')}</h1>
                <select required value={selectedServer || "All"} onChange={(e) => setSelectedServer(e.target.value)} className={`select select-sm select-bordered select-md w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 ${selectedServer !== "All" ? "border-blue-400" : "border-gray-300 dark:border-gray-500"}`}>
                    <option value={"All"}>{t('All')}</option>
                    {Object.values(servers).map((el, index) => (<option key={index} value={el.id}>{el.title}</option>))}
                </select>
            </div>
            <div className="flex flex-col gap-1">
                <h1>{t('times.perma')}</h1>
                <input type="checkbox" className="toggle toggle-info" onChange={(e) => setIsPerma(e.target.checked)} />
            </div>

            <div className="flex flex-col gap-2 my-auto">
                <div className="flex gap-1">
                    <button className={`btn btn-sm btn-square ${isHighlight && !isDefault && !isAdvanced ? "border-2 border-green-500 dark:border-sky-400" : "border-none"} bg-primary dark:bg-blue-600 dark:hover:bg-primary text-white hover:bg-primary/80`} title={t("Search")} onClick={handleRegularSearch}><IoSearch /></button>
                    <button className={`btn btn-sm btn-square  ${isHighlight && isDefault && !isAdvanced ? "border-2 border-green-500 dark:border-sky-400" : "border-none"} bg-primary dark:bg-blue-600 dark:hover:bg-primary text-white hover:bg-primary/80 `} title={t("Default")} onClick={handleDefault}><MdRestartAlt size={18} /></button>
                    <button className="btn btn-sm btn-square border-none bg-white text-primary hover:bg-gray-200 border border-primary" title={t("Clear")} onClick={handleClear}><AiOutlineClear size={16} /></button>
                </div>

            </div>

        </div>
    )
}